/**
 * Storage Service
 * Handles localStorage operations for card actions
 */

import { CardActionData, CardActionsStorage, CardAction } from '../types';

/**
 * Base key prefix for localStorage
 */
const STORAGE_KEY_PREFIX = 'actionbase-card-actions';

/**
 * Service for managing card action data in localStorage
 * Each audit has its own isolated storage
 */
export class StorageService {
  private storageKey: string;

  /**
   * Create a storage service instance
   * @param auditName - Optional audit name for namespaced storage
   */
  constructor(auditName?: string) {
    this.storageKey = auditName
      ? `${STORAGE_KEY_PREFIX}:${auditName}`
      : STORAGE_KEY_PREFIX;
  }

  /**
   * Set the audit name to namespace the storage
   * @param auditName - The audit name
   */
  setAuditName(auditName: string): void {
    this.storageKey = `${STORAGE_KEY_PREFIX}:${auditName}`;
  }

  /**
   * Get the current storage key
   * @returns The storage key being used
   */
  getStorageKey(): string {
    return this.storageKey;
  }

  /**
   * Generate a unique key for a card action
   * @param checkKey - The check identifier
   * @param deviceType - The device type
   * @returns Unique action key
   */
  generateActionKey(checkKey: string, deviceType: string): string {
    return `${checkKey}::${deviceType}`;
  }

  /**
   * Save a card action to localStorage
   * @param data - Card action data to save
   */
  saveAction(data: CardActionData): void {
    const existing = this.getAllActions();
    const actionKey = this.generateActionKey(data.checkKey, data.deviceType);
    existing[actionKey] = data;
    this.persistActions(existing);
  }

  /**
   * Remove a card action from localStorage
   * @param checkKey - The check identifier
   * @param deviceType - The device type
   */
  removeAction(checkKey: string, deviceType: string): void {
    const existing = this.getAllActions();
    const actionKey = this.generateActionKey(checkKey, deviceType);
    delete existing[actionKey];
    this.persistActions(existing);
  }

  /**
   * Get a specific card action
   * @param checkKey - The check identifier
   * @param deviceType - The device type
   * @returns Card action data or null if not found
   */
  getAction(checkKey: string, deviceType: string): CardActionData | null {
    const existing = this.getAllActions();
    const actionKey = this.generateActionKey(checkKey, deviceType);
    return existing[actionKey] || null;
  }

  /**
   * Get all stored card actions
   * @returns All card actions keyed by action key
   */
  getAllActions(): CardActionsStorage {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return {};
    }
  }

  /**
   * Get all actions as an array
   * @returns Array of card action data
   */
  getAllActionsArray(): CardActionData[] {
    return Object.values(this.getAllActions());
  }

  /**
   * Check if any actions have been saved
   * @returns True if at least one action exists
   */
  hasActions(): boolean {
    return Object.keys(this.getAllActions()).length > 0;
  }

  /**
   * Get count of saved actions
   * @returns Number of saved actions
   */
  getActionCount(): number {
    return Object.keys(this.getAllActions()).length;
  }

  /**
   * Clear all saved actions
   */
  clearActions(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Get actions grouped by action type
   * @returns Actions grouped by CardAction type
   */
  getActionsGroupedByType(): Record<CardAction, CardActionData[]> {
    const actions = this.getAllActionsArray();
    const grouped: Record<CardAction, CardActionData[]> = {
      'thumbs-up': [],
      'thumbs-down': [],
      'done': [],
      'not-applicable': []
    };

    actions.forEach(action => {
      if (grouped[action.action]) {
        grouped[action.action].push(action);
      }
    });

    return grouped;
  }

  /**
   * Get actions grouped by area/category
   * @returns Actions grouped by area
   */
  getActionsGroupedByArea(): Record<string, CardActionData[]> {
    const actions = this.getAllActionsArray();
    const grouped: Record<string, CardActionData[]> = {};

    actions.forEach(action => {
      if (!grouped[action.area]) {
        grouped[action.area] = [];
      }
      grouped[action.area].push(action);
    });

    return grouped;
  }

  /**
   * Persist actions to localStorage
   * @param actions - Actions to persist
   */
  private persistActions(actions: CardActionsStorage): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(actions));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }
}
